import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Key, Image, Type, Loader2, Info, Globe, Sparkles, Download, HelpCircle, Sun, Moon, Monitor, ChevronDown, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { useTheme } from '../contexts/ThemeContext';
import { IconLegend } from './IconLegend';
import { SnoozeDigest } from './SnoozeDigest';
import { PendingSyncSettings } from './PendingSyncSettings';
import { SyncHistorySettings } from './SyncHistorySettings';
import { OfflineCacheManager } from './OfflineCacheManager';
import { Switch } from './ui/switch';

import { API_BASE_URL } from '../utils/apiBase';
const BACKEND_URL = API_BASE_URL;

/**
 * Compact "N minutes/hours/days ago" formatter for the backfill history line.
 * Uses `Intl.RelativeTimeFormat` so it's locale-aware. Returns a fallback of
 * just the ISO date if the runtime lacks support.
 */
function formatRelativeTime(iso, language = 'en') {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.round((then - now) / 1000);
    const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'auto' });
    const abs = Math.abs(diffSec);
    if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
    if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
    if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day');
    if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month');
    return rtf.format(Math.round(diffSec / 31536000), 'year');
  } catch (_e) {
    return new Date(iso).toLocaleDateString();
  }
}

const LANGUAGES = [
  { code: 'en', label: 'English (US)' },
  { code: 'es', label: 'Espa\u00f1ol' },
  { code: 'fr', label: 'Fran\u00e7ais' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Portugu\u00eas' },
  { code: 'ja', label: '\u65e5\u672c\u8a9e' },
  { code: 'ko', label: '\ud55c\uad6d\uc5b4' },
  { code: 'zh', label: '\u4e2d\u6587' },
];

// Care-detail fields the user can pick from in the "Fill missing information"
// section. Keys must match `_ALL_CARE_FIELDS` in backend/server.py; labels are
// deliberately friendly, not the raw JSON key. Toxicity + toxicity_notes and
// dormancy_notes + dormancy_months are collapsed into a single pill each
// because they always co-fill on the AI side.
const CARE_FIELD_GROUPS = [
  { id: 'watering',     labelKey: 'careField.watering',    label: 'Watering',      fields: ['watering'] },
  { id: 'light',        labelKey: 'careField.light',       label: 'Light',         fields: ['light'] },
  { id: 'humidity',     labelKey: 'careField.humidity',    label: 'Humidity',      fields: ['humidity'] },
  { id: 'temperature',  labelKey: 'careField.temperature', label: 'Temperature',   fields: ['temperature'] },
  { id: 'toxicity',     labelKey: 'careField.toxicity',    label: 'Toxicity',      fields: ['toxicity', 'toxicity_notes'] },
  { id: 'environment',  labelKey: 'careField.environment', label: 'Environment',   fields: ['environment'] },
  { id: 'growth_rate',  labelKey: 'careField.growthRate',  label: 'Growth rate',   fields: ['growth_rate'] },
  { id: 'family_type',  labelKey: 'careField.familyType',  label: 'Family & type', fields: ['family', 'plant_type'] },
  { id: 'dormancy',     labelKey: 'careField.dormancy',    label: 'Dormancy',      fields: ['dormancy_notes', 'dormancy_months'] },
  { id: 'blooming',     labelKey: 'careField.blooming',    label: 'Blooming',      fields: ['bloom_season', 'bloom_duration', 'blooms_indoors', 'bloom_tips'] },
];
const _ALL_FIELD_IDS = CARE_FIELD_GROUPS.map((g) => g.id);

// Module-scoped run flag + poll interval. These survive the SettingsModal's
// mount/unmount lifecycle so a server-side backfill job started in one modal
// session keeps being polled and its completion toast keeps firing even
// after the user closes the modal ("continue in background" flow).
//
// Iter 67: the backfill loop itself now runs SERVER-SIDE (see the
// /api/plants/backfill/{start,status,cancel,acknowledge} endpoints), so the
// browser tab can be suspended / phone locked / app switched without
// killing the job. This singleton just tracks the client-side POLL loop
// that mirrors server progress into the UI + summary toast.
const _backfillControl = {
  running: false,
  pollIntervalId: null,
  lastJob: null,
  // Signalled by the poll loop on completion. If the modal is still
  // mounted, it handles the reload/UI update directly. If the modal has
  // already been closed (background mode), we still want the user to see
  // that fresh data landed — so we surface a "Reload now" toast with an
  // action button instead of hard-reloading the whole page from under
  // whatever screen they navigated to.
  onCompleteWhenClosed: null,
};

export const SettingsModal = ({ onClose, isLocalMode = false, onExportCSV, onExportPDF, onOpenTour, plants = [], reviewLaterDupePairs = null, onRestoreDupePair, onPermaDismissDupePair, permanentlyNotDupePairs = null, onResetDupePairs, isOwner = false, dupeBannerEnabled = true, onToggleDupeBanner, dupeStrictness = 'balanced', onChangeDupeStrictness, pendingSyncCount = 0, pendingFlushing = false, onFlushPendingSync, onOpenPendingSync }) => {
  const { fontSize, setFontSize, theme, setTheme } = useTheme();
  const { language: currentLang, setLanguage, t, unsupportedLocale } = useLanguage();
  const [loading, setLoading] = useState(!isLocalMode);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('csv');
  const [toastPosition, setToastPosition] = useState(() => {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem('plant_toast_position') : null;
    return v === 'bottom-center' ? 'bottom-center' : 'top-center';
  });
  const [settings, setSettings] = useState({
    gemini_api_key: '',
    photo_quality: 'compressed',
    font_size: fontSize,
    language: currentLang,
    auto_backfill_on_add: true,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [refreshingAi, setRefreshingAi] = useState(() => _backfillControl.running);
  // Confirm-close intercept: if the user tries to close the modal while a
  // backfill loop is in flight, we show this dialog asking them to choose
  // between letting it run in the background or stopping at the current
  // chunk. Value is `null` when hidden, otherwise a bag with the pending
  // close action + a snapshot of how many plants were filled so far.
  const [closeConfirm, setCloseConfirm] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Dry-run preview result: { total_needs_update, field_breakdown } | null
  const [backfillPreview, setBackfillPreview] = useState(null);
  // Persisted "last refreshed" summary shown under the refresh button.
  // Stored in localStorage as { at: ISOString, count: number } after every
  // successful backfill loop that actually updated one or more plants.
  const [lastBackfill, setLastBackfill] = useState(() => {
    try {
      const raw = localStorage.getItem('plant_last_backfill');
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });
  const [showLegend, setShowLegend] = useState(false);
  // Duplicate-review list is collapsed by default so a long "Review later"
  // backlog doesn't fill the Settings sheet on open. User expands it
  // explicitly to triage.
  const [showDupeReview, setShowDupeReview] = useState(false);
  // "Fill missing info" — which field groups the user wants to backfill.
  // Default: everything ticked. Advanced links are collapsed by default and
  // only get revealed via an explicit toggle so the initial view stays calm.
  const [selectedFieldIds, setSelectedFieldIds] = useState(() => new Set(_ALL_FIELD_IDS));
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // iter 56 — Backfill Estimator. `estimator` holds the per-field breakdown
  // from a lightweight auto-preview (dry_run, no AI calls) fired on modal
  // open. Feeds a live "~7s × N plants ≈ X min" line under the button so
  // users know the time cost BEFORE tapping Check.
  const [estimator, setEstimator] = useState(null);
  // iter 56 — Progress Persistence. `resumableScan` shows a "Resume where
  // you left off" banner if a previous scan was interrupted (Cloudflare
  // timeout, tab close, budget-exhausted). Populated from localStorage on
  // mount and cleared once the user resumes or dismisses.
  const [resumableScan, setResumableScan] = useState(() => {
    try {
      const raw = localStorage.getItem('plant_backfill_in_progress');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Age gate: 24h. Anything older is stale (user probably resolved it
      // another way) and we quietly discard.
      if (!parsed?.at || (Date.now() - new Date(parsed.at).getTime()) > 86_400_000) {
        localStorage.removeItem('plant_backfill_in_progress');
        return null;
      }
      return parsed;
    } catch (_e) {
      return null;
    }
  });
  // Set to true when the backfill loop hit the daily AI budget. Drives a
  // highlighted callout above the Gemini API key field so the user can
  // paste their own key and keep going without waiting for UTC midnight.
  const [budgetExhaustedRetryHours, setBudgetExhaustedRetryHours] = useState(null);
  const apiKeyInputRef = useRef(null);

  useEffect(() => {
    if (!isLocalMode) loadSettings();
  }, [isLocalMode]);

  // iter 56 — Estimator auto-preview. One-shot dry_run on modal open so
  // the "About Xs × N plants" line under the button has real numbers to
  // work with before the user taps Check. Drive-mode only — local mode
  // has no server-side backfill flow to estimate.
  useEffect(() => {
    if (isLocalMode) return;
    let cancelled = false;
    (async () => {
      try {
        const sessionToken = localStorage.getItem('auth_hint');
        if (!sessionToken) return;
        const resp = await axios.post(
          `${BACKEND_URL}/api/plants/backfill-missing-care-details?dry_run=true`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
        );
        if (cancelled) return;
        setEstimator({
          total_needs_update: resp.data?.total_needs_update || 0,
          field_breakdown: resp.data?.field_breakdown || {},
        });
      } catch (error) {
        // Silent — the estimator is a nice-to-have. If it fails, the user
        // still gets accurate numbers by clicking Check what needs filling.
        console.debug('Backfill estimator preview skipped:', error?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isLocalMode]);

  // iter 67 — Backfill job resume. On modal open, check whether a
  // server-side backfill is currently running or recently completed but
  // not yet acknowledged. If running, resume the polling loop so live
  // progress reflects in the UI. If completed while the user was away,
  // fire the summary toast now instead of losing that feedback.
  useEffect(() => {
    if (isLocalMode) return;
    let cancelled = false;
    (async () => {
      try {
        const sessionToken = localStorage.getItem('auth_hint');
        if (!sessionToken) return;
        const resp = await axios.get(
          `${BACKEND_URL}/api/plants/backfill/status`,
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 15000 }
        );
        if (cancelled) return;
        const job = resp.data?.job;
        if (!job) return;
        if (job.status === 'running') {
          setRefreshingAi(true);
          _startBackfillPoll(job);
        } else if (!job.acknowledged) {
          // Terminal state the user hasn't seen yet — surface it now.
          _handleTerminalBackfillJob(job);
        }
      } catch (error) {
        console.debug('Backfill resume-check skipped:', error?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [isLocalMode]);

  const loadSettings = async () => {
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      const response = await axios.get(`${BACKEND_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSettings({
        gemini_api_key: response.data.gemini_api_key || '',
        photo_quality: response.data.photo_quality || 'compressed',
        font_size: response.data.font_size || fontSize,
        language: response.data.language || localStorage.getItem('plant_app_language') || 'en',
        auto_backfill_on_add: response.data.auto_backfill_on_add !== false,
      });
      setInitialApiKey(response.data.gemini_api_key || '');
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // The snapshot of gemini_api_key loaded from the server, used to detect
  // whether the user actually changed the key before spending a validation
  // API call. If the key is unchanged, skip the round-trip.
  const [initialApiKey, setInitialApiKey] = useState('');

  const handleSave = async () => {
    try {
      setSaving(true);
      // Always persist language via context (updates localStorage + triggers re-render)
      setLanguage(settings.language);

      if (isLocalMode) {
        // Local mode: just persist font size
        setFontSize(settings.font_size);
        toast.success('Settings saved');
        onClose();
        return;
      }

      const sessionToken = localStorage.getItem('auth_hint');
      const trimmedKey = (settings.gemini_api_key || '').trim();

      // Validate the pasted key before persisting — a silently-bad key
      // would only surface as a failed backfill later. Only validate if
      // the user actually changed the field and it's not empty (empty
      // means "use shared key", which is always valid).
      if (trimmedKey && trimmedKey !== initialApiKey) {
        try {
          const testResp = await axios.post(
            `${BACKEND_URL}/api/settings/test-gemini-key`,
            { api_key: trimmedKey },
            {
              headers: { Authorization: `Bearer ${sessionToken}` },
              timeout: 20000,
            }
          );
          if (!testResp.data?.valid) {
            const msg = testResp.data?.message || 'Key rejected by Google.';
            toast.error(`Gemini key check failed: ${msg}`);
            return; // Don't persist — keep the user on the modal to fix.
          }
          toast.success('Gemini key verified — saving…', { duration: 2000 });
        } catch (validateErr) {
          console.error('Key validation failed:', validateErr);
          const detail = validateErr?.response?.data?.detail || validateErr?.message || 'unknown';
          toast.error(`Could not verify Gemini key: ${detail}`);
          return; // Fail-closed — don't persist an unverified key.
        }
      }

      await axios.put(
        `${BACKEND_URL}/api/settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );

      // Update font size in context
      setFontSize(settings.font_size);

      // Remember the freshly-persisted key so a subsequent save without
      // any change to the field doesn't re-validate.
      setInitialApiKey(trimmedKey);

      // Auto-resume paused backfill: if the callout was up (budget exhausted
      // with the shared key) and the user just pasted a valid own key, kick
      // the backfill loop again automatically instead of making them find
      // the "Check" button. Fires AFTER a brief pause so the "saved" toast
      // is visible first.
      const shouldAutoResume = trimmedKey && budgetExhaustedRetryHours !== null;
      if (shouldAutoResume) {
        setBudgetExhaustedRetryHours(null);
        toast.success('Key saved — resuming backfill…', { duration: 3000 });
        // Keep the modal open so the user can see the progress toast.
        setTimeout(async () => {
          const preview = await handlePreviewRefresh();
          // If preview shows there IS still work to do, auto-confirm to
          // continue the paused backfill without a second click. Pass the
          // fresh total so the progress toast reflects the actual count
          // rather than a stale closure value.
          if (preview && (preview.total_needs_update || 0) > 0) {
            handleConfirmRefresh(preview.total_needs_update);
          }
        }, 600);
        return; // Skip onClose — user needs to see resumed backfill
      }

      toast.success('Settings saved successfully');
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFontSizeChange = (size) => {
    setSettings({ ...settings, font_size: size });
    setFontSize(size); // Apply immediately
  };

  const handleDownloadLibrary = async () => {
    if (downloading) return;
    try {
      setDownloading(true);
      if (downloadFormat === 'pdf') {
        await onExportPDF?.();
      } else {
        await onExportCSV?.();
      }
    } finally {
      setDownloading(false);
    }
  };

  // Backfill the new "environment" + "toxicity_notes" (reptile-aware) fields
  // for existing plants. Only fills plants that are missing those fields.
  // Drive-mode only — local-mode users can refresh individual plants via the
  // "AI Auto-fill" button on the Edit form.
  //
  // Two-step flow:
  //  1. `handlePreviewRefresh` fires a `dry_run=true` call — no AI, no writes —
  //     just counts. The response is rendered as a confirmation card so the
  //     user sees exactly how many AI calls they're about to spend.
  //  2. `handleConfirmRefresh` runs the chunked loop (default 8 plants per
  //     request, well inside Cloudflare's 100-second edge timeout), updating
  //     a live progress toast until the server reports remaining=0.
  // Turn the currently-ticked field-group IDs into the flat backend field
  // list (e.g. { toxicity } → toxicity,toxicity_notes). Falls back to
  // sending no filter when the user ticked every group (identical to the
  // legacy "fill everything missing" behavior).
  const _buildFieldsParam = () => {
    if (selectedFieldIds.size === _ALL_FIELD_IDS.length) return '';
    const flat = [];
    CARE_FIELD_GROUPS.forEach((g) => {
      if (selectedFieldIds.has(g.id)) flat.push(...g.fields);
    });
    return flat.length ? `&fields=${encodeURIComponent(flat.join(','))}` : '';
  };

  const handlePreviewRefresh = async () => {
    if (previewing || refreshingAi) return null;
    // Zero fields ticked — treat as a no-op with a friendly nudge.
    if (selectedFieldIds.size === 0) {
      toast.warning(t('careLog.fillPickAtLeastOne') || 'Pick at least one field to fill.');
      return null;
    }
    try {
      setPreviewing(true);
      const sessionToken = localStorage.getItem('auth_hint');
      const fieldsParam = _buildFieldsParam();
      const resp = await axios.post(
        `${BACKEND_URL}/api/plants/backfill-missing-care-details?dry_run=true${fieldsParam}`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionToken}` },
          timeout: 60000,
        }
      );
      const data = resp.data || {};
      const preview = {
        total_needs_update: data.total_needs_update || 0,
        field_breakdown: data.field_breakdown || {},
        total: data.total || 0,
      };
      if (preview.total_needs_update === 0) {
        toast.success('All plants already have the latest care details.');
        setBackfillPreview(null);
        return preview;
      }
      setBackfillPreview(preview);
      return preview;
    } catch (error) {
      console.error('Backfill preview failed:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Couldn't check: ${msg}`);
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  // ============================================================
  // Server-side background backfill (iter 67)
  // ============================================================
  // The chunked client-driven loop this replaced would die whenever the
  // phone locked or the user switched apps — mobile browsers suspend
  // in-flight fetches within seconds. We now just kick off a server-side
  // job and poll for status. Job survives client disconnect entirely.
  //
  //   handleConfirmRefresh → POST /api/plants/backfill/start
  //   _startPollingBackfill → GET /api/plants/backfill/status every 5s
  //   closeAndStop → POST /api/plants/backfill/cancel
  //   _handleTerminalJob → POST /api/plants/backfill/acknowledge (once)
  //
  // The module-scoped `_backfillControl.pollIntervalId` survives the
  // modal's mount/unmount cycle so "continue in background" keeps polling
  // and firing the completion toast even after the user closes Settings.
  const _showBackfillProgressToast = (job) => {
    const total = job.total || 0;
    const filled = job.filled || 0;
    const remaining = job.remaining ?? Math.max(0, total - filled);
    // NOTE: The visible progress indicator lives in the draggable
    // BackfillNotifierChip so it doesn't block the Settings gear on
    // mobile. We still persist a compact resumable record here so the
    // "Resume your last fill" banner on the next SettingsModal open
    // reflects live server progress.
    try {
      localStorage.setItem('plant_backfill_in_progress', JSON.stringify({
        at: job.started_at || new Date().toISOString(),
        filled,
        remaining,
        total_at_start: total,
        fields: _buildFieldsParam(),
      }));
    } catch (_e) { /* localStorage quota; ignore */ }
  };

  const _showBackfillSummaryToast = (job) => {
    const totalUpdated = job.filled || 0;
    const aggregatedFills = job.aggregated_fills || {};
    const topFields = Object.entries(aggregatedFills)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6);
    toast.custom(
      () => (
        <div
          className="rounded-lg shadow-lg p-3 max-w-sm"
          style={{ backgroundColor: 'hsl(143, 28%, 96%)', border: '1px solid hsl(143, 28%, 80%)', color: 'hsl(143, 28%, 20%)' }}
          data-testid="backfill-summary-toast"
        >
          <p className="text-sm font-medium mb-2">
            {`Filled ${totalUpdated} plant${totalUpdated !== 1 ? 's' : ''}. Refreshing…`}
          </p>
          {topFields.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {topFields.map(([field, count]) => (
                <span
                  key={field}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]"
                  style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 82%)' }}
                >
                  <span className="font-medium">{field}</span>
                  <span style={{ color: 'hsl(125, 12%, 40%)' }}>× {count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ),
      { duration: 5000 }
    );
  };

  const _acknowledgeBackfillJob = () => {
    // Fire-and-forget — the client already knows the job is done, we're
    // just telling the server "don't return this one on the next poll".
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      axios.post(
        `${BACKEND_URL}/api/plants/backfill/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 10000 }
      ).catch(() => { /* best-effort */ });
    } catch (_e) { /* ignore */ }
  };

  const _handleTerminalBackfillJob = (job) => {
    toast.dismiss('backfill-progress');
    _acknowledgeBackfillJob();
    const filled = job.filled || 0;
    if (job.status === 'complete') {
      if (filled > 0) {
        try {
          const entry = { at: job.finished_at || new Date().toISOString(), count: filled };
          localStorage.setItem('plant_last_backfill', JSON.stringify(entry));
          setLastBackfill(entry);
        } catch (_e) { /* ignore */ }
        // First-time-only silent mangled-text repair (unchanged from iter 55).
        try {
          const already = localStorage.getItem('plant_mangled_text_repaired') === 'true';
          if (!already) {
            const sessionToken = localStorage.getItem('auth_hint');
            axios.post(
              `${BACKEND_URL}/api/plants/repair-mangled-text`,
              {},
              { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
            )
              .then(() => { localStorage.setItem('plant_mangled_text_repaired', 'true'); })
              .catch((err) => { console.debug('First-time mangled text repair skipped:', err?.message); });
          }
        } catch (_e) { /* ignore */ }
        _showBackfillSummaryToast(job);
        if (_backfillControl.onCompleteWhenClosed) {
          _backfillControl.onCompleteWhenClosed({ totalUpdated: filled });
          _backfillControl.onCompleteWhenClosed = null;
        } else {
          setTimeout(() => window.location.reload(), 1500);
        }
      } else {
        toast.success('All fillable care details are already up to date.');
      }
      try { localStorage.removeItem('plant_backfill_in_progress'); } catch (_e) { /* ignore */ }
      setResumableScan(null);
    } else if (job.status === 'stopped') {
      toast.info(
        `Backfill stopped. ${filled} plant${filled !== 1 ? 's' : ''} filled so far — reopen Settings to resume.`,
        { duration: 5000 }
      );
      if (filled > 0) {
        try {
          const entry = { at: new Date().toISOString(), count: filled };
          localStorage.setItem('plant_last_backfill', JSON.stringify(entry));
          setLastBackfill(entry);
        } catch (_e) { /* ignore */ }
      }
    } else if (job.status === 'budget_exhausted') {
      const retrySec = parseInt(job.budget_retry_after || '0', 10);
      const hours = retrySec > 0 ? Math.max(1, Math.ceil(retrySec / 3600)) : null;
      setBudgetExhaustedRetryHours(hours);
      requestAnimationFrame(() => {
        if (apiKeyInputRef.current) {
          apiKeyInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          apiKeyInputRef.current.focus();
        }
      });
      toast.warning(
        hours
          ? `Daily AI budget reached. ${filled} plants filled so far. Paste your own Gemini key below to keep going, or wait ~${hours}h for UTC-midnight reset.`
          : `Daily AI budget reached. ${filled} plants filled so far. Paste your own Gemini key below to keep going, or wait for UTC midnight.`,
        { duration: 8000 }
      );
      if (filled > 0) {
        try {
          const entry = { at: new Date().toISOString(), count: filled };
          localStorage.setItem('plant_last_backfill', JSON.stringify(entry));
          setLastBackfill(entry);
        } catch (_e) { /* ignore */ }
      }
    } else if (job.status === 'failed') {
      toast.error(`Failed to refresh: ${job.error || 'Unknown error'}`);
    }
    setRefreshingAi(false);
    _backfillControl.running = false;
    _backfillControl.lastJob = null;
  };

  const _stopBackfillPoll = () => {
    if (_backfillControl.pollIntervalId) {
      clearInterval(_backfillControl.pollIntervalId);
      _backfillControl.pollIntervalId = null;
    }
  };

  const _startBackfillPoll = (initialJob) => {
    // Ensure only one poll runs at a time.
    _stopBackfillPoll();
    _backfillControl.running = true;
    _backfillControl.lastJob = initialJob;
    if (initialJob) _showBackfillProgressToast(initialJob);
    const tick = async () => {
      try {
        const sessionToken = localStorage.getItem('auth_hint');
        const resp = await axios.get(
          `${BACKEND_URL}/api/plants/backfill/status`,
          { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 15000 }
        );
        const job = resp.data?.job;
        if (!job) {
          // Server has no active job — likely acknowledged already. Stop.
          _stopBackfillPoll();
          setRefreshingAi(false);
          _backfillControl.running = false;
          toast.dismiss('backfill-progress');
          return;
        }
        _backfillControl.lastJob = job;
        if (job.status === 'running') {
          _showBackfillProgressToast(job);
        } else {
          _stopBackfillPoll();
          _handleTerminalBackfillJob(job);
        }
      } catch (err) {
        // Transient network error — keep polling, the server job is
        // unaffected. Only log at debug so we don't spam the console.
        console.debug('Backfill status poll failed (will retry):', err?.message);
      }
    };
    // Fire once immediately for snappy UI, then every 5s.
    _backfillControl.pollIntervalId = setInterval(tick, 5000);
    tick();
  };

  const handleConfirmRefresh = async (_initialTotal = null) => {
    if (refreshingAi) return;
    const sessionToken = localStorage.getItem('auth_hint');
    try {
      setRefreshingAi(true);
      setBackfillPreview(null); // hide the preview card immediately
      // `_buildFieldsParam()` returns `&fields=...` (with leading `&`) because
      // the dry-run endpoint already has `?dry_run=true` before it. This URL
      // has no other query param, so we must convert the leading `&` to `?`
      // — otherwise it becomes part of the PATH and FastAPI 404s ("Not Found").
      const rawFieldsParam = _buildFieldsParam();
      const startUrl = rawFieldsParam
        ? `${BACKEND_URL}/api/plants/backfill/start?${rawFieldsParam.slice(1)}`
        : `${BACKEND_URL}/api/plants/backfill/start`;
      const resp = await axios.post(
        startUrl,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 30000 }
      );
      const job = resp.data;
      if (!job || job.status === 'complete') {
        // Nothing to fill.
        setRefreshingAi(false);
        toast.success('All fillable care details are already up to date.');
        try { localStorage.removeItem('plant_backfill_in_progress'); } catch (_e) { /* ignore */ }
        setResumableScan(null);
        return;
      }
      if (job.status === 'running') {
        _startBackfillPoll(job);
      }
    } catch (error) {
      setRefreshingAi(false);
      _backfillControl.running = false;
      console.error('Failed to start backfill job:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to start refresh: ${msg}`);
    }
  };

  const handleResetMarkers = async (scope = 'all') => {
    if (refreshingAi || previewing) return;
    const msg = scope === 'dormancy'
      ? (t('resetMarkersDormancyConfirm') || 'This will clear the dormancy "checked" mark on every plant so the next scan can re-ask the AI. Continue?')
      : (t('resetMarkersConfirm') || 'This will clear every backfill "checked" mark on every plant so the next scan can re-ask the AI about them. Continue?');
    if (!window.confirm(msg)) return;
    try {
      setPreviewing(true);
      const sessionToken = localStorage.getItem('auth_hint');
      const resp = await axios.post(
        `${BACKEND_URL}/api/plants/reset-backfill-markers?fields=${encodeURIComponent(scope)}`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionToken}` },
          timeout: 60000,
        }
      );
      const n = resp.data?.reset || 0;
      if (n > 0) {
        toast.success(`Cleared skip markers on ${n} plant${n !== 1 ? 's' : ''}. Click "Check" to re-scan.`);
      } else {
        toast.success('No skip markers to clear.');
      }
    } catch (error) {
      console.error('Reset markers failed:', error);
      const errMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed: ${errMsg}`);
    } finally {
      setPreviewing(false);
    }
  };

  // One-shot cleanup that repairs any legacy corrupt "Nov-Febleaves" style
  // strings in stored `dormancy_notes` / `toxicity_notes`. The frontend
  // already softens these at display time, but this endpoint updates the
  // underlying data so CSV / PDF exports and search see the clean text too.
  const handleRepairText = async () => {
    if (refreshingAi || previewing) return;
    if (!window.confirm(t('repairTextConfirm') || 'Repair any corrupt dormancy / toxicity text on your plants? Existing values are only re-spaced, never rewritten by AI. Safe to run any time.')) return;
    try {
      setPreviewing(true);
      const sessionToken = localStorage.getItem('auth_hint');
      const resp = await axios.post(
        `${BACKEND_URL}/api/plants/repair-mangled-text`,
        {},
        {
          headers: { Authorization: `Bearer ${sessionToken}` },
          timeout: 60000,
        }
      );
      const touched = resp.data?.plants_touched || 0;
      const repaired = resp.data?.repaired || 0;
      if (touched > 0) {
        toast.success(`Repaired ${repaired} field${repaired !== 1 ? 's' : ''} across ${touched} plant${touched !== 1 ? 's' : ''}. Refreshing…`);
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast.success('No mangled text found — you\'re all clean.');
      }
    } catch (error) {
      console.error('Repair text failed:', error);
      const errMsg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed: ${errMsg}`);
    } finally {
      setPreviewing(false);
    }
  };

  // Close intercept — if the user tries to close the modal while a
  // backfill is in flight, offer them the choice between letting it keep
  // running in the background or stopping at the current chunk. All other
  // closes pass through unchanged.
  const handleOpenChange = (nextOpen) => {
    if (nextOpen) return; // opening never needs intercepting
    if (_backfillControl.running) {
      setCloseConfirm({ open: true });
      return;
    }
    onClose();
  };

  // "Continue in background" — wire up the completion callback so when the
  // loop eventually finishes we don't hard-reload the whole page (the user
  // may have navigated to Library / Archive etc). Instead surface a small
  // "Backfill complete — Reload" toast with an action button.
  const closeBackground = () => {
    _backfillControl.onCompleteWhenClosed = ({ totalUpdated }) => {
      toast.custom(
        (id) => (
          <div
            className="rounded-lg shadow-lg p-3 max-w-sm flex items-start gap-3"
            style={{ backgroundColor: 'hsl(143, 28%, 96%)', border: '1px solid hsl(143, 28%, 80%)', color: 'hsl(143, 28%, 20%)' }}
            data-testid="backfill-background-complete-toast"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {t('backfillBgDoneTitle')?.replace('{count}', totalUpdated) ||
                  `Backfill complete — ${totalUpdated} plant${totalUpdated !== 1 ? 's' : ''} updated.`}
              </p>
              <p className="text-xs mt-1" style={{ color: 'hsl(125, 12%, 40%)' }}>
                {t('backfillBgDoneHint') || 'Reload to see the fresh care details.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-xs px-2.5 py-1 rounded"
              style={{ backgroundColor: 'hsl(143, 45%, 34%)', color: 'white' }}
              data-testid="backfill-background-reload-btn"
            >
              {t('reloadNow') || 'Reload'}
            </button>
            <button
              type="button"
              onClick={() => toast.dismiss(id)}
              className="text-xs opacity-60 hover:opacity-100 px-1"
              aria-label={t('dismiss') || 'Dismiss'}
              data-testid="backfill-background-dismiss-btn"
            >
              ×
            </button>
          </div>
        ),
        { duration: 20000 }
      );
    };
    setCloseConfirm(null);
    onClose();
  };

  // "Stop now" — call the server-side cancel endpoint. The running job
  // checks `cancel_requested` at each chunk boundary and exits cleanly.
  // Also stop the client-side poll immediately so the UI reflects the
  // stop without waiting for the next poll tick.
  const closeAndStop = () => {
    try {
      const sessionToken = localStorage.getItem('auth_hint');
      axios.post(
        `${BACKEND_URL}/api/plants/backfill/cancel`,
        {},
        { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 10000 }
      ).catch(() => { /* best-effort; the poll will notice a stopped state anyway */ });
    } catch (_e) { /* ignore */ }
    setCloseConfirm(null);
    onClose();
  };

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="settings-modal" style={{ backgroundColor: 'white' }}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-display" style={{ color: 'hsl(143, 28%, 25%)' }}>
            Settings
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'hsl(143, 28%, 25%)' }} />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Gemini API Key — authenticated only */}
            {!isLocalMode && (
            <div className="space-y-2">
              <Label htmlFor="gemini_api_key" className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Key className="w-4 h-4" />
                Google Gemini API Key
              </Label>

              {/* Own-key nudge — appears whenever the user hit the shared-key
                  daily budget during a backfill in this Settings session. Auto-
                  focuses + scrolls the input into view so the flow is one-click:
                  paste key → hit Save. Dismissable but sticky by design so users
                  can't miss it once triggered. */}
              {budgetExhaustedRetryHours !== null && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2.5"
                  style={{ backgroundColor: 'hsl(40, 90%, 96%)', border: '1px solid hsl(40, 80%, 80%)' }}
                  role="status"
                  aria-live="polite"
                  data-testid="own-key-nudge"
                >
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(40, 80%, 45%)' }} />
                  <div className="flex-1 min-w-0 text-xs leading-relaxed" style={{ color: 'hsl(40, 30%, 22%)' }}>
                    <p className="font-medium mb-1" style={{ color: 'hsl(40, 30%, 15%)' }}>
                      Daily AI budget reached
                    </p>
                    <p className="mb-2">
                      Paste your own Gemini key below to keep filling care details right away — takes 30 seconds and stays free for personal use.{budgetExhaustedRetryHours ? ` Otherwise the shared budget resets in ~${budgetExhaustedRetryHours}h.` : ''}
                    </p>
                    <a
                      href="https://aistudio.google.com/app/apikey"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 underline underline-offset-2 font-medium"
                      style={{ color: 'hsl(40, 80%, 35%)' }}
                      data-testid="own-key-nudge-link"
                    >
                      Get a free Gemini key at Google AI Studio →
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBudgetExhaustedRetryHours(null)}
                    className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors"
                    style={{ color: 'hsl(40, 40%, 40%)' }}
                    aria-label="Dismiss"
                    data-testid="own-key-nudge-dismiss"
                  >
                    <span aria-hidden="true" className="text-lg leading-none">×</span>
                  </button>
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  ref={apiKeyInputRef}
                  id="gemini_api_key"
                  type={showApiKey ? 'text' : 'password'}
                  value={settings.gemini_api_key}
                  onChange={(e) => {
                    setSettings({ ...settings, gemini_api_key: e.target.value });
                    // If the user pastes a key, clear the nudge on the fly.
                    if (e.target.value && budgetExhaustedRetryHours !== null) {
                      setBudgetExhaustedRetryHours(null);
                    }
                  }}
                  placeholder="Leave empty to use default key"
                  className="border-2"
                  style={{
                    borderColor: budgetExhaustedRetryHours !== null ? 'hsl(40, 80%, 55%)' : 'hsl(143, 28%, 85%)',
                    color: 'hsl(125, 12%, 20%)',
                  }}
                  data-testid="gemini-api-key-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </Button>
              </div>
              <p className="text-sm flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 57%)' }} />
                <span>Leave empty to use the built-in key. Add your own key from Google AI Studio for unlimited usage.</span>
              </p>
            </div>
            )}

            {/* Photo Quality — authenticated only */}
            {!isLocalMode && (
            <div className="space-y-2">
              <Label htmlFor="photo_quality" className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Image className="w-4 h-4" />
                Photo Quality
              </Label>
              <Select
                value={settings.photo_quality}
                onValueChange={(value) => setSettings({ ...settings, photo_quality: value })}
              >
                <SelectTrigger id="photo_quality" data-testid="photo-quality-select" className="border-2" style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(125, 12%, 20%)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Quality (larger files)</SelectItem>
                  <SelectItem value="compressed">Compressed (recommended)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                Compressed photos save space in your Google Drive while maintaining good quality.
              </p>
            </div>
            )}

            {/* Language */}
            <div className="space-y-2">
              <Label htmlFor="language" className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Globe className="w-4 h-4" />
                Language
              </Label>
              <Select
                value={settings.language}
                onValueChange={(value) => setSettings({ ...settings, language: value })}
              >
                <SelectTrigger id="language" data-testid="language-select" className="border-2" style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(125, 12%, 20%)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                Sets the display language for the app. Default is English (US).
              </p>
              {unsupportedLocale && (
                <p
                  className="flex items-start gap-1.5 text-xs px-1"
                  style={{ color: 'hsl(125, 12%, 40%)' }}
                  data-testid="lang-unsupported-note"
                >
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>
                    {(t('langUnsupportedNote') || 'Your device language ({locale}) isn\u2019t supported yet, so we\u2019re showing English. Pick a language above any time.')
                      .replace('{locale}', (() => {
                        try {
                          return new Intl.DisplayNames([currentLang], { type: 'language' }).of(unsupportedLocale) || unsupportedLocale;
                        } catch {
                          return unsupportedLocale;
                        }
                      })())}
                  </span>
                </p>
              )}
            </div>

            {/* Theme (Light / Dark / Auto) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Moon className="w-4 h-4" />
                {t('themeLabel') || 'Appearance'}
              </Label>
              <div className="flex gap-2" role="radiogroup" aria-label={t('themeLabel') || 'Appearance'}>
                {[
                  { id: 'light', icon: Sun, label: t('themeLight') || 'Light', testid: 'theme-light' },
                  { id: 'dark',  icon: Moon, label: t('themeDark') || 'Dark',  testid: 'theme-dark' },
                  { id: 'auto',  icon: Monitor, label: t('themeAuto') || 'Auto', testid: 'theme-auto' },
                ].map(({ id, icon: Icon, label, testid }) => {
                  const active = theme === id;
                  return (
                    <Button
                      key={id}
                      type="button"
                      variant={active ? 'default' : 'outline'}
                      role="radio"
                      aria-checked={active}
                      onClick={() => setTheme(id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5"
                      style={active
                        ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }
                        : { borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                      data-testid={testid}
                    >
                      <Icon className="w-4 h-4" aria-hidden="true" />
                      <span>{label}</span>
                    </Button>
                  );
                })}
              </div>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                {t('themeHelp') || 'Dark mode uses color inversion to keep photos accurate. Auto follows your system setting.'}
              </p>
            </div>

            {/* Font Size */}
            <div className="space-y-2">
              <Label htmlFor="font_size" className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Type className="w-4 h-4" />
                Font Size
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={settings.font_size === 'small' ? 'default' : 'outline'}
                  onClick={() => handleFontSizeChange('small')}
                  className="flex-1"
                  style={settings.font_size === 'small' ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' } : { borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                  data-testid="font-size-small"
                >
                  A-
                </Button>
                <Button
                  type="button"
                  variant={settings.font_size === 'medium' ? 'default' : 'outline'}
                  onClick={() => handleFontSizeChange('medium')}
                  className="flex-1"
                  style={settings.font_size === 'medium' ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' } : { borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                  data-testid="font-size-medium"
                >
                  A
                </Button>
                <Button
                  type="button"
                  variant={settings.font_size === 'large' ? 'default' : 'outline'}
                  onClick={() => handleFontSizeChange('large')}
                  className="flex-1"
                  style={settings.font_size === 'large' ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' } : { borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                  data-testid="font-size-large"
                >
                  A+
                </Button>
              </div>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                Adjust text size for better readability. Does not affect layout.
              </p>
            </div>

            {/* Toast position — top vs bottom (iter 94). Persisted in
                localStorage; a custom event notifies the ThemedToaster in
                App.js so the change takes effect live without a reload. */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                {t('toastPositionLabel') || 'Toast position'}
              </Label>
              <div className="flex gap-2" role="radiogroup" aria-label={t('toastPositionLabel') || 'Toast position'}>
                {[
                  { value: 'top-center', labelKey: 'toastPositionTop', fallback: 'Top' },
                  { value: 'bottom-center', labelKey: 'toastPositionBottom', fallback: 'Bottom' },
                ].map(opt => {
                  const active = toastPosition === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      type="button"
                      variant={active ? 'default' : 'outline'}
                      role="radio"
                      aria-checked={active}
                      onClick={() => {
                        setToastPosition(opt.value);
                        try { localStorage.setItem('plant_toast_position', opt.value); } catch { /* quota */ }
                        try { window.dispatchEvent(new Event('plant-toast-position')); } catch { /* older browsers */ }
                        // Inline preview: defer so ThemedToaster applies the
                        // new position before the sample toast renders, and
                        // reuse one id so rapid taps don't stack.
                        setTimeout(() => {
                          toast.success(t('toastPositionPreview') || 'Sample toast — this is where notifications will appear', { id: 'toast-position-preview' });
                        }, 60);
                      }}
                      className="flex-1"
                      style={active
                        ? { backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }
                        : { borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }
                      }
                      data-testid={`toast-position-${opt.value}`}
                    >
                      {t(opt.labelKey) || opt.fallback}
                    </Button>
                  );
                })}
              </div>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                {t('toastPositionBlurb') || 'Where confirmation and error toasts appear on the screen.'}
              </p>
            </div>

            {/* Download library — CSV or PDF */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                <Download className="w-4 h-4" />
                {t('downloadLibrary') || 'Download library'}
              </Label>
              <div className="flex gap-2">
                <Select value={downloadFormat} onValueChange={setDownloadFormat}>
                  <SelectTrigger id="download-format" data-testid="download-format-select" aria-label={t('downloadFormatLabel') || 'Download format'} className="border-2 flex-1" style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(125, 12%, 20%)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv" data-testid="download-format-csv">CSV</SelectItem>
                    <SelectItem value="pdf" data-testid="download-format-pdf">PDF</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleDownloadLibrary}
                  disabled={downloading}
                  className="flex-shrink-0"
                  style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                  data-testid="download-library-button"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('downloading') || 'Downloading…'}
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      {t('download') || 'Download'}
                    </>
                  )}
                </Button>
              </div>
              <p className="text-sm p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                {t('downloadLibraryDesc') || 'CSV is best for reimporting into spreadsheets or other tools. PDF gives a formatted printable copy with proper support for non-Latin plant names.'}
              </p>
            </div>

            {/* Fill missing AI info — dry-runs first to show the user a
                clear preview of how many AI calls they're about to spend,
                then runs the chunked backfill loop. Field picker + Advanced
                collapse were added in iter 55 to keep the initial view calm
                and let users fill just watering (or just light, etc.) rather
                than committing to a full-library scan every time. */}
            {!isLocalMode && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                  <Sparkles className="w-4 h-4" />
                  {t('fillMissingAiTitle') || 'Fill in any missing AI-provided information'}
                </Label>

                {/* Confirmation card — shown after a successful dry-run.
                    Renders the total plant count + a per-field breakdown so
                    the user knows exactly what they're spending AI budget on
                    before pulling the trigger. */}
                {backfillPreview ? (
                  <div
                    className="p-3 rounded-lg space-y-3"
                    style={{ backgroundColor: 'hsl(40, 60%, 96%)', border: '1px solid hsl(40, 50%, 85%)' }}
                    data-testid="backfill-preview-card"
                  >
                    <div className="text-sm" style={{ color: 'hsl(40, 30%, 22%)' }}>
                      <p className="font-medium mb-1">
                        {backfillPreview.total_needs_update === 1
                          ? '1 plant is missing this info.'
                          : `${backfillPreview.total_needs_update} plants are missing this info.`}
                      </p>
                      <p className="text-xs" style={{ color: 'hsl(40, 20%, 35%)' }}>
                        Approx. {backfillPreview.total_needs_update} AI call{backfillPreview.total_needs_update !== 1 ? 's' : ''} · ~{Math.max(1, Math.ceil(backfillPreview.total_needs_update * 7 / 60))} min to complete
                      </p>
                    </div>

                    {Object.keys(backfillPreview.field_breakdown).length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid="backfill-field-breakdown">
                        {Object.entries(backfillPreview.field_breakdown)
                          .sort(([, a], [, b]) => b - a)
                          .map(([field, count]) => (
                            <span
                              key={field}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                              style={{ backgroundColor: 'white', border: '1px solid hsl(40, 50%, 85%)', color: 'hsl(40, 30%, 22%)' }}
                            >
                              <span className="font-medium">{field}</span>
                              <span style={{ color: 'hsl(40, 20%, 45%)' }}>× {count}</span>
                            </span>
                          ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleConfirmRefresh}
                        disabled={refreshingAi}
                        className="flex-1"
                        style={{ backgroundColor: 'hsl(143, 28%, 45%)', color: 'white' }}
                        data-testid="backfill-confirm-button"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {t('fillMissingAiConfirm') || 'Fill them'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => setBackfillPreview(null)}
                        disabled={refreshingAi}
                        variant="outline"
                        style={{ borderColor: 'hsl(40, 50%, 85%)', color: 'hsl(40, 30%, 22%)' }}
                        data-testid="backfill-cancel-button"
                      >
                        {t('cancel') || 'Cancel'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Choose fields to fill — collapsible so the default UI
                        stays a single button. Every group ticked by default
                        matches the legacy "fill everything" behavior. */}
                    <button
                      type="button"
                      onClick={() => setShowFieldPicker((v) => !v)}
                      className="w-full flex items-center justify-between text-left text-xs px-3 py-2 rounded-lg transition-colors"
                      style={{ backgroundColor: 'hsl(82, 55%, 96%)', border: '1px solid hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
                      data-testid="fill-field-picker-toggle"
                      aria-expanded={showFieldPicker}
                    >
                      <span>
                        {selectedFieldIds.size === _ALL_FIELD_IDS.length
                          ? (t('fillFieldsAll') || 'Fill every missing field')
                          : (t('fillFieldsCount') || 'Fill only: {n} field(s)').replace('{n}', selectedFieldIds.size)}
                      </span>
                      <span className="text-xs" style={{ color: 'hsl(143, 28%, 30%)' }}>
                        {showFieldPicker ? '▴' : (t('choose') || 'Choose ▾')}
                      </span>
                    </button>

                    {showFieldPicker && (
                      <div
                        className="p-3 rounded-lg space-y-2"
                        style={{ backgroundColor: 'white', border: '1px solid hsl(143, 28%, 85%)' }}
                        data-testid="fill-field-picker"
                      >
                        <div className="flex items-center justify-between text-xs">
                          <button
                            type="button"
                            onClick={() => setSelectedFieldIds(new Set(_ALL_FIELD_IDS))}
                            className="underline underline-offset-2 hover:opacity-80"
                            style={{ color: 'hsl(143, 28%, 30%)' }}
                            data-testid="fill-field-picker-all"
                          >
                            {t('selectAll') || 'Select all'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedFieldIds(new Set())}
                            className="underline underline-offset-2 hover:opacity-80"
                            style={{ color: 'hsl(125, 12%, 40%)' }}
                            data-testid="fill-field-picker-none"
                          >
                            {t('clearAll') || 'Clear'}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {CARE_FIELD_GROUPS.map((g) => {
                            const checked = selectedFieldIds.has(g.id);
                            return (
                              <label
                                key={g.id}
                                className="flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer transition-colors"
                                style={{
                                  backgroundColor: checked ? 'hsl(143, 28%, 96%)' : 'transparent',
                                  border: `1px solid ${checked ? 'hsl(143, 28%, 75%)' : 'hsl(143, 28%, 92%)'}`,
                                  color: 'hsl(143, 28%, 25%)',
                                }}
                                data-testid={`fill-field-${g.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    // Set-based state — avoid the closure-stale mutation trap by
                                    // building a fresh Set from the previous value each time.
                                    setSelectedFieldIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(g.id); else next.delete(g.id);
                                      return next;
                                    });
                                  }}
                                  className="accent-current"
                                  style={{ accentColor: 'hsl(143, 28%, 45%)' }}
                                  aria-label={t(g.labelKey) || g.label}
                                />
                                <span>{t(g.labelKey) || g.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={handlePreviewRefresh}
                      disabled={previewing || refreshingAi || selectedFieldIds.size === 0}
                      className="w-full"
                      style={{ backgroundColor: 'hsl(143, 28%, 45%)', color: 'white' }}
                      data-testid="refresh-ai-care-button"
                    >
                      {refreshingAi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('fillMissingAiRunning') || 'Filling in details… this may take several minutes'}
                        </>
                      ) : previewing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('fillMissingAiChecking') || 'Checking your library…'}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t('fillMissingAiButton') || 'Check what needs filling'}
                        </>
                      )}
                    </Button>

                    {/* iter 56 — Backfill Estimator. Live "About Xs × N plants
                        ≈ Y min" line so users see the time cost BEFORE
                        tapping Check. When the user narrows the field
                        selection, we sum the per-field breakdown as an
                        upper-bound estimate (over-counts plants missing
                        multiple selected fields — a "≤" is added to the
                        copy in that case so it's honest). */}
                    {estimator && (estimator.total_needs_update > 0) && !refreshingAi && !previewing && (
                      (() => {
                        const isAll = selectedFieldIds.size === _ALL_FIELD_IDS.length;
                        // Sum of per-field counts across only the selected groups.
                        // Over-counts vs. actual unique plants when multiple picked
                        // fields overlap in the same plant, so we clamp to the total.
                        let estCount;
                        if (isAll) {
                          estCount = estimator.total_needs_update;
                        } else {
                          let sum = 0;
                          CARE_FIELD_GROUPS.forEach((g) => {
                            if (!selectedFieldIds.has(g.id)) return;
                            g.fields.forEach((f) => { sum += estimator.field_breakdown[f] || 0; });
                          });
                          estCount = Math.min(sum, estimator.total_needs_update);
                        }
                        if (!estCount) return null;
                        const SECS_PER = 7;
                        const totalSec = estCount * SECS_PER;
                        const timeStr = totalSec < 60
                          ? `${totalSec} sec`
                          : totalSec < 3600
                            ? `~${Math.max(1, Math.round(totalSec / 60))} min`
                            : `~${(totalSec / 3600).toFixed(1)} hr`;
                        const approx = isAll ? '~' : '≤ ';
                        return (
                          <p
                            className="text-[11px] text-center"
                            style={{ color: 'hsl(125, 12%, 35%)' }}
                            data-testid="backfill-estimator"
                          >
                            {(t('backfillEstimator') || 'About {sec}s per plant × {count} plants ≈ {time}')
                              .replace('{sec}', SECS_PER)
                              .replace('{count}', `${approx}${estCount}`)
                              .replace('{time}', timeStr)}
                          </p>
                        );
                      })()
                    )}

                    {/* iter 56 — Resume banner. Populated on modal mount if
                        a previous scan was interrupted (Cloudflare timeout,
                        budget exhausted, tab close). One-tap "Continue" or
                        "Dismiss". Age-gated to 24 h via the localStorage
                        parser above. */}
                    {resumableScan && !refreshingAi && !previewing && (
                      <div
                        className="p-2 rounded-lg text-xs flex items-start gap-2"
                        style={{ backgroundColor: 'hsl(40, 70%, 96%)', border: '1px solid hsl(40, 60%, 82%)', color: 'hsl(40, 30%, 22%)' }}
                        data-testid="backfill-resume-banner"
                      >
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: 'hsl(40, 70%, 40%)' }} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">
                            {(t('backfillResumeHeader') || 'Resume your last fill')}
                          </p>
                          <p style={{ color: 'hsl(40, 25%, 30%)' }}>
                            {(t('backfillResumeDetail') || '{filled} filled, {remaining} still to go from {ago}.')
                              .replace('{filled}', resumableScan.filled || 0)
                              .replace('{remaining}', resumableScan.remaining ?? '?')
                              .replace('{ago}', formatRelativeTime(resumableScan.at, currentLang))}
                          </p>
                          <div className="flex gap-3 mt-1">
                            <button
                              type="button"
                              onClick={() => {
                                // Resume with the same field filter the interrupted
                                // scan was using. Falls back to current selection
                                // if the persisted fields param is empty (== all).
                                setResumableScan(null);
                                handleConfirmRefresh(resumableScan.remaining || null);
                              }}
                              className="underline underline-offset-2 hover:opacity-80 font-medium"
                              style={{ color: 'hsl(143, 28%, 30%)' }}
                              data-testid="backfill-resume-continue"
                            >
                              {t('backfillResumeContinue') || 'Continue'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                try { localStorage.removeItem('plant_backfill_in_progress'); } catch (_e) { /* ignore */ }
                                setResumableScan(null);
                              }}
                              className="underline underline-offset-2 hover:opacity-80"
                              style={{ color: 'hsl(125, 12%, 40%)' }}
                              data-testid="backfill-resume-dismiss"
                            >
                              {t('dismiss') || 'Dismiss'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* iter 56 — Auto-backfill on add. Small toggle row. Off
                        by default. When on, adding a plant fires a silent
                        single-plant backfill in the background. */}
                    <label
                      className="flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer"
                      style={{ color: 'hsl(143, 28%, 25%)' }}
                      data-testid="auto-backfill-toggle-label"
                    >
                      <input
                        type="checkbox"
                        checked={!!settings.auto_backfill_on_add}
                        onChange={(e) => setSettings({ ...settings, auto_backfill_on_add: e.target.checked })}
                        style={{ accentColor: 'hsl(143, 28%, 45%)' }}
                        data-testid="auto-backfill-toggle"
                      />
                      <span>{t('autoBackfillOnAdd') || 'Auto-fill missing details when adding a new plant'}</span>
                    </label>
                  </>
                )}

                {/* Last-run summary — populated after a successful backfill.
                    Uses `Intl.RelativeTimeFormat` for a locale-aware
                    "2 days ago" instead of a raw timestamp. */}
                {lastBackfill && lastBackfill.at && !backfillPreview && !refreshingAi && (
                  <p
                    className="text-xs text-center pt-0.5"
                    style={{ color: 'hsl(125, 12%, 35%)' }}
                    data-testid="backfill-last-summary"
                  >
                    {(t('backfillLastRun') || 'Last refreshed')}: {formatRelativeTime(lastBackfill.at, currentLang)} · {lastBackfill.count} {lastBackfill.count === 1 ? (t('plant') || 'plant') : (t('plants') || 'plants')} {t('backfillLastUpdated') || 'updated'}
                  </p>
                )}

                {/* Advanced — hidden by default. Only reveals once the user
                    has run a successful backfill (so first-time users see a
                    clean, non-intimidating view). Contains the "re-scan"
                    tools that force the AI to redo work it already did. */}
                {lastBackfill && lastBackfill.at && !backfillPreview && !refreshingAi && (
                  <div className="pt-1" data-testid="backfill-advanced-wrapper">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced((v) => !v)}
                      className="text-[11px] underline underline-offset-2 hover:opacity-80"
                      style={{ color: 'hsl(125, 12%, 40%)' }}
                      data-testid="backfill-advanced-toggle"
                      aria-expanded={showAdvanced}
                    >
                      {showAdvanced
                        ? (t('advancedHide') || 'Hide advanced')
                        : (t('advancedShow') || 'Advanced options')}
                    </button>
                    {showAdvanced && (
                      <div
                        className="mt-2 p-2 rounded-lg space-y-1.5 text-xs"
                        style={{ backgroundColor: 'hsl(0, 0%, 98%)', border: '1px solid hsl(143, 28%, 90%)' }}
                        data-testid="backfill-advanced-panel"
                      >
                        <p style={{ color: 'hsl(125, 12%, 35%)' }}>
                          {t('advancedIntro') || 'Force a re-scan of details the AI already answered. Costs extra AI calls.'}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button
                            type="button"
                            onClick={() => handleResetMarkers('dormancy')}
                            disabled={previewing || refreshingAi}
                            className="underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                            style={{ color: 'hsl(21, 70%, 32%)' }}
                            data-testid="reset-backfill-markers-dormancy"
                          >
                            {t('resetMarkersLinkDormancy') || 'Re-scan dormancy'}
                          </button>
                          <span style={{ color: 'hsl(125, 12%, 65%)' }}>·</span>
                          <button
                            type="button"
                            onClick={() => handleResetMarkers('all')}
                            disabled={previewing || refreshingAi}
                            className="underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                            style={{ color: 'hsl(21, 70%, 32%)' }}
                            data-testid="reset-backfill-markers-all"
                          >
                            {t('resetMarkersLinkAll') || 'Re-scan everything'}
                          </button>
                          <span style={{ color: 'hsl(125, 12%, 65%)' }}>·</span>
                          <button
                            type="button"
                            onClick={handleRepairText}
                            disabled={previewing || refreshingAi}
                            className="underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
                            style={{ color: 'hsl(21, 70%, 32%)' }}
                            data-testid="repair-mangled-text"
                          >
                            {t('repairTextLink') || 'Repair mangled text'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-sm flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'hsl(82, 55%, 95%)', color: 'hsl(125, 12%, 25%)' }}>
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'hsl(21, 70%, 40%)' }} />
                  <span>{t('fillMissingAiInfo') || "Asks the AI to fill in any missing care details (watering, light, family, environment, etc.). Existing values are never overwritten. Runs in small chunks so it works cleanly on large libraries — safe to stop and resume any time."}</span>
                </p>
              </div>
            )}

            {/* Icon Legend — one-tap glossary of every icon used across the
                care details and library filters. Kept collapsible so it doesn't
                bloat the modal on first open. */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowLegend(v => !v)}
                className="w-full flex items-center justify-between gap-2 text-base font-medium py-1"
                style={{ color: 'hsl(143, 28%, 25%)' }}
                aria-expanded={showLegend}
                aria-controls="settings-icon-legend-content"
                data-testid="settings-icon-legend-toggle"
              >
                <span className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" aria-hidden="true" />
                  {t('iconLegend') || 'Icon legend'}
                </span>
                <span className="text-sm font-normal" style={{ color: 'hsl(125, 12%, 32%)' }}>
                  {showLegend ? (t('hide') || 'Hide') : (t('show') || 'Show')}
                </span>
              </button>
              {showLegend && (
                <div
                  id="settings-icon-legend-content"
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: 'hsl(82, 55%, 97%)', border: '1px solid hsl(143, 28%, 90%)' }}
                  data-testid="settings-icon-legend-content"
                >
                  <IconLegend />
                </div>
              )}
            </div>

            {/* Snooze Digest — lists every currently-snoozed banner
                (duplicates + per-family AI tips + all-AI-tips) so users
                can un-hide them on demand. Renders nothing when nothing
                is snoozed. */}
            <SnoozeDigest />

            {/* Offline sync queue mirror — reuses LibraryPage's queue
                instance via props. Hidden when nothing is pending. */}
            {!isLocalMode && (
              <PendingSyncSettings
                count={pendingSyncCount}
                flushing={pendingFlushing}
                onFlush={onFlushPendingSync}
                onOpen={onOpenPendingSync}
              />
            )}

            {/* Offline cache manager — Drive mode only. Shows the mirror
                size + a one-tap clear button. Hidden entirely when no
                snapshot exists yet. */}
            {!isLocalMode && <OfflineCacheManager />}

            {/* Recent sync activity log — Drive mode only. */}
            {!isLocalMode && <SyncHistorySettings />}

            {/* Offline & Sync help — explains what queues offline vs.
                needs a connection. Drive mode only. */}
            {!isLocalMode && (
              <div className="space-y-1.5 border-t pt-4" data-testid="offline-help-section">
                <div className="flex items-center gap-1.5 text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                  <Info className="w-4 h-4" aria-hidden="true" />
                  {t('offlineHelpTitle') || 'Offline & Sync'}
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'hsl(125, 12%, 40%)' }}>
                  {t('offlineHelpBody') || 'Offline you can browse and edit, archive or delete plants — those changes queue and sync automatically when you reconnect. Adding a brand-new plant needs a connection.'}
                  {' '}
                  <Link
                    to="/about#offline-sync"
                    className="underline hover:opacity-80"
                    style={{ color: 'hsl(143, 28%, 38%)' }}
                    data-testid="offline-help-learn-more"
                  >
                    {t('offlineHelpLearnMore') || 'Learn more'}
                  </Link>
                </p>
              </div>
            )}

            {/* Duplicate detection controls — master switch + strictness
                slider. Sits above the review-later list so users can
                silence the banner or dial the fuzzy matcher down before
                digging into individual pairs. */}
            <div className="space-y-3 border-t pt-4" data-testid="settings-dupe-controls">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium" style={{ color: 'hsl(143, 28%, 25%)' }}>
                    {t('dupeBannerToggleLabel') || 'Suggest duplicate merges'}
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(125, 12%, 40%)' }}>
                    {t('dupeBannerToggleBlurb') || 'When off, the library never shows the "possible duplicates" banner. Merging via bulk-edit or plant details still works.'}
                  </p>
                </div>
                <Switch
                  checked={dupeBannerEnabled}
                  onCheckedChange={(v) => onToggleDupeBanner?.(v)}
                  aria-label={t('dupeBannerToggleLabel') || 'Suggest duplicate merges'}
                  data-testid="settings-dupe-banner-switch"
                />
              </div>
              {dupeBannerEnabled && (
                <div>
                  <div className="text-sm font-medium mb-1" style={{ color: 'hsl(143, 28%, 25%)' }}>
                    {t('dupeStrictnessLabel') || 'Detection sensitivity'}
                  </div>
                  <div
                    className="inline-flex items-center rounded-md overflow-hidden"
                    style={{ border: '1px solid hsl(143, 28%, 65%)' }}
                    role="radiogroup"
                    aria-label={t('dupeStrictnessLabel') || 'Detection sensitivity'}
                  >
                    {[
                      { value: 'balanced', labelKey: 'dupeStrictnessBalanced', fallback: 'Balanced' },
                      { value: 'strict', labelKey: 'dupeStrictnessStrict', fallback: 'Strict' },
                      { value: 'exact', labelKey: 'dupeStrictnessExact', fallback: 'Exact only' },
                    ].map(opt => {
                      const active = dupeStrictness === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => onChangeDupeStrictness?.(opt.value)}
                          className="text-xs px-3 py-1.5 transition-colors"
                          style={{
                            backgroundColor: active ? 'hsl(143, 28%, 25%)' : 'white',
                            color: active ? 'white' : 'hsl(143, 28%, 25%)',
                          }}
                          data-testid={`settings-dupe-strictness-${opt.value}`}
                        >
                          {t(opt.labelKey) || opt.fallback}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] mt-1" style={{ color: 'hsl(125, 12%, 45%)' }}>
                    {dupeStrictness === 'exact'
                      ? (t('dupeStrictnessExactBlurb') || 'Only suggests pairs with identical names. Nothing fuzzy.')
                      : dupeStrictness === 'strict'
                        ? (t('dupeStrictnessStrictBlurb') || 'Both names must share most of their words (≥ 85%).')
                        : (t('dupeStrictnessBalancedBlurb') || 'Default. Suggests pairs whose names substantially overlap.')}
                  </p>
                </div>
              )}
            </div>

            {/* Duplicate pair review — surfaces pairs the user tagged
                "Review later" from the library banner. From here they
                can bring one back to the library for another look, or
                dismiss it permanently ("not a duplicate"). Hidden when
                the review-later bucket is empty. Collapsed by default so
                a long backlog doesn't dominate the Settings sheet. */}
            {reviewLaterDupePairs && reviewLaterDupePairs.size > 0 && (() => {
              // Resolve the pair keys to actual plants; skip any that
              // no longer exist (e.g., one plant was deleted / merged
              // some other way).
              const rows = [];
              for (const pairKey of reviewLaterDupePairs) {
                const ids = String(pairKey).split('|');
                if (ids.length !== 2) continue;
                const a = plants.find(p => p.plant_id === ids[0]);
                const b = plants.find(p => p.plant_id === ids[1]);
                if (!a || !b) continue;
                const aName = (a.common_name || '').trim();
                const bName = (b.common_name || '').trim();
                const [target, source] = aName.length <= bName.length ? [a, b] : [b, a];
                rows.push({ pairKey, target, source });
              }
              if (rows.length === 0) return null;
              return (
                <div className="space-y-2 border-t pt-4" data-testid="settings-dupe-review">
                  <button
                    type="button"
                    onClick={() => setShowDupeReview(v => !v)}
                    className="w-full flex items-center justify-between gap-2 text-base font-medium text-left transition-colors hover:opacity-80"
                    style={{ color: 'hsl(143, 28%, 25%)' }}
                    aria-expanded={showDupeReview}
                    aria-controls="settings-dupe-review-content"
                    data-testid="settings-dupe-review-toggle"
                  >
                    <span className="flex items-center gap-2">
                      {showDupeReview ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
                      {t('dupeReviewSettingsTitle') || 'Review later — duplicate suggestions'}
                    </span>
                    <span
                      className="text-xs font-normal px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: 'hsl(143, 28%, 90%)', color: 'hsl(143, 28%, 25%)' }}
                      data-testid="settings-dupe-review-count"
                    >
                      {rows.length}
                    </span>
                  </button>
                  {showDupeReview && (
                    <div id="settings-dupe-review-content" className="space-y-2">
                      <p className="text-xs" style={{ color: 'hsl(125, 12%, 40%)' }}>
                        {t('dupeReviewSettingsBlurb') || 'Pairs you asked to review later. Bring one back to the library banner or dismiss it permanently.'}
                      </p>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof window !== 'undefined' && window.confirm(
                              (t('dupeMarkAllNotDupeConfirm') || 'Mark all {n} pairs as "Not a duplicate"? They will not be suggested again.').replace('{n}', rows.length)
                            )) {
                              // PermaDismiss every pair in one shot. Uses
                              // the same handler as the per-row button so
                              // localStorage + server sync stay consistent.
                              rows.forEach(({ pairKey }) => onPermaDismissDupePair?.(pairKey));
                              setShowDupeReview(false);
                            }
                          }}
                          className="text-xs px-2.5 py-1.5 rounded-md"
                          style={{ backgroundColor: 'white', color: 'hsl(143, 28%, 25%)', border: '1px solid hsl(143, 28%, 65%)' }}
                          data-testid="settings-dupe-mark-all-not-dupe"
                        >
                          {(t('dupeMarkAllNotDupe') || 'Mark all as "Not a duplicate" ({n})').replace('{n}', rows.length)}
                        </button>
                      )}
                      <div className="space-y-1.5">
                        {rows.map(({ pairKey, target, source }) => (
                          <div
                            key={pairKey}
                            className="flex flex-wrap items-center gap-2 rounded-md px-2.5 py-2"
                            style={{ backgroundColor: 'hsl(143, 28%, 96%)', border: '1px solid hsl(143, 28%, 88%)' }}
                            data-testid={`settings-dupe-row-${pairKey}`}
                          >
                            <span className="text-xs flex-1 min-w-0 truncate" style={{ color: 'hsl(143, 28%, 25%)' }}>
                              &ldquo;{target.common_name}&rdquo; &harr; &ldquo;{source.common_name}&rdquo;
                            </span>
                            <button
                              type="button"
                              onClick={() => onRestoreDupePair?.(pairKey)}
                              className="text-xs px-2 py-1 rounded-md"
                              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                              data-testid={`settings-dupe-restore-${pairKey}`}
                            >
                              {t('dupeReviewRestore') || 'Bring back'}
                            </button>
                            <button
                              type="button"
                              onClick={() => onPermaDismissDupePair?.(pairKey)}
                              className="text-xs px-2 py-1 rounded-md"
                              style={{ color: 'hsl(125, 12%, 35%)', backgroundColor: 'white', border: '1px solid hsl(143, 28%, 82%)' }}
                              data-testid={`settings-dupe-perma-${pairKey}`}
                            >
                              {t('dupeNotADuplicate') || 'Not a duplicate'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Reset all duplicate suggestions — OWNER-ONLY nuclear
                option, gated by /api/auth/me's `is_owner` flag which is
                derived from the OWNER_EMAILS env var. Hidden from every
                other user because this is only meant as an admin lever
                to unstick the historical over-aggressive fuzzy matcher
                bloat — regular users get natural per-pair dismissal via
                "Not a duplicate" / "Review later" and don't need a
                nuclear reset. */}
            {isOwner && onResetDupePairs && (
              (permanentlyNotDupePairs && permanentlyNotDupePairs.size > 0) ||
              (reviewLaterDupePairs && reviewLaterDupePairs.size > 0)
            ) && (
              <div className="border-t pt-4" data-testid="settings-dupe-reset">
                <button
                  type="button"
                  onClick={() => {
                    const total = (permanentlyNotDupePairs?.size || 0) + (reviewLaterDupePairs?.size || 0);
                    if (typeof window !== 'undefined' && window.confirm(
                      (t('dupeResetConfirm') || 'Clear {n} dismissed duplicate suggestions and start fresh?').replace('{n}', total)
                    )) {
                      onResetDupePairs();
                    }
                  }}
                  className="text-xs underline"
                  style={{ color: 'hsl(0, 60%, 40%)' }}
                  data-testid="settings-dupe-reset-btn"
                >
                  {t('dupeResetAll') || 'Reset all duplicate suggestions'}
                </button>
                <p className="text-[11px] mt-1" style={{ color: 'hsl(125, 12%, 45%)' }}>
                  {t('dupeResetBlurb') || 'Wipes both the "Not a duplicate" and "Review later" lists so the banner starts flagging pairs again from scratch.'}
                </p>
              </div>
            )}

            {/* Watch onboarding tour — always available so users can revisit
                the first-run introduction whenever they want. Sits next to the
                icon legend since both are learning-focused. */}
            {onOpenTour && (
              <button
                type="button"
                onClick={onOpenTour}
                className="w-full flex items-center justify-between gap-2 text-base font-medium py-1 text-left transition-colors hover:opacity-80"
                style={{ color: 'hsl(143, 28%, 25%)' }}
                data-testid="settings-watch-tour-btn"
              >
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" aria-hidden="true" />
                  {t('watchOnboardingTour') || 'Watch onboarding tour'}
                </span>
                <span className="text-sm font-normal" style={{ color: 'hsl(125, 12%, 32%)' }}>
                  {t('watch') || 'Watch'}
                </span>
              </button>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1"
                style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
                style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
                data-testid="save-settings-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
      {/* Backfill-in-flight close confirm. Only rendered when the user tries
          to close the parent Settings modal while a backfill loop is
          actively running. Radix Dialog stacks portals cleanly, so this
          inner Dialog appears above the parent without any focus tug-of-war. */}
      {closeConfirm && (
        <Dialog open={true} onOpenChange={() => setCloseConfirm(null)}>
          <DialogContent className="max-w-sm" data-testid="backfill-close-confirm" style={{ backgroundColor: 'white' }}>
            <DialogHeader>
              <DialogTitle style={{ color: 'hsl(143, 28%, 25%)' }}>
                {t('backfillRunningTitle') || 'Backfill in progress'}
              </DialogTitle>
              <DialogDescription style={{ color: 'hsl(125, 12%, 32%)' }}>
                {t('backfillRunningBody') ||
                  'The AI is still filling missing care details. Would you like it to keep running in the background, or stop at the current point?'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse sm:justify-start">
              <button
                type="button"
                onClick={closeBackground}
                className="text-sm px-3 py-2 rounded"
                style={{ backgroundColor: 'hsl(143, 45%, 34%)', color: 'white' }}
                data-testid="backfill-continue-background-btn"
              >
                {t('backfillContinueBackground') || 'Continue in background'}
              </button>
              <button
                type="button"
                onClick={closeAndStop}
                className="text-sm px-3 py-2 rounded border"
                style={{ backgroundColor: 'white', color: 'hsl(0, 60%, 40%)', borderColor: 'hsl(0, 60%, 82%)' }}
                data-testid="backfill-stop-now-btn"
              >
                {t('backfillStopNow') || 'Stop now'}
              </button>
              <button
                type="button"
                onClick={() => setCloseConfirm(null)}
                className="text-sm px-3 py-2 rounded"
                style={{ backgroundColor: 'transparent', color: 'hsl(125, 12%, 40%)' }}
                data-testid="backfill-close-cancel-btn"
              >
                {t('cancel') || 'Cancel'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};
